// ===== 账号登录 HTTP 客户端 =====
// 对接同步服务的账号体系（MySQL users / auth_tokens 表）：
//   POST /api/auth/login   { username, password }  → { token, user }
//   GET  /api/auth/me      Bearer token             → { user }
//   POST /api/auth/logout  Bearer token
//   GET  /api/admin/users        Bearer（仅 boss）→ { users }
//   POST /api/admin/users        Bearer（仅 boss）新增账号
//   POST /api/admin/users/update Bearer（仅 boss）改密/改角色
//   POST /api/admin/users/delete Bearer（仅 boss）删除账号
//
// 服务器地址：优先取同步配置里的 serverUrl；未配置时用当前站点同源
//（工作台前端与同步服务部署在同一域名下，由 Nginx 反代 /api/*）。
// 所有请求带超时，绝不裸 fetch（项目铁律）。

import { getSyncConfig } from './syncConfig';

export interface AuthUser {
  id: number;
  username: string;
  role: string;
  displayName: string;
}

export interface AuthSession {
  token: string;
  user: AuthUser;
  savedAt: number;
}

export class AuthApiError extends Error {
  status: number; // 0 = 网络不可达
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

const AUTH_KEY = 'changfa_auth';
export const AUTH_EVENT = 'changfa:auth-changed';

function baseUrl(): string {
  const cfg = getSyncConfig();
  return (cfg?.serverUrl || window.location.origin).replace(/\/+$/, '');
}

async function authRequest<T>(
  path: string,
  opts: { token?: string; body?: unknown; method?: 'GET' | 'POST'; timeoutMs?: number } = {},
): Promise<T> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), opts.timeoutMs ?? 12000);
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (opts.token) headers['Authorization'] = `Bearer ${opts.token}`;
    const res = await fetch(baseUrl() + path, {
      method: opts.method ?? (opts.body === undefined ? 'GET' : 'POST'),
      headers,
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
      signal: ctrl.signal,
    });
    if (!res.ok) {
      let detail = '';
      try { detail = (await res.json() as { error?: string }).error || ''; } catch { /* ignore */ }
      throw new AuthApiError(detail || `HTTP ${res.status}`, res.status);
    }
    return (await res.json()) as T;
  } catch (e) {
    if (e instanceof AuthApiError) throw e;
    // abort / 网络错误 → status 0，调用方可据此进入「离线模式」
    throw new AuthApiError(e instanceof Error && e.message === 'The user aborted a request.' ? '连接超时' : '无法连接服务器', 0);
  } finally {
    clearTimeout(timer);
  }
}

// ---------- 会话存取 ----------

export function loadSession(): AuthSession | null {
  try {
    const raw = localStorage.getItem(AUTH_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as AuthSession;
    return s && s.token && s.user ? s : null;
  } catch {
    return null;
  }
}

export function saveSession(s: AuthSession): void {
  localStorage.setItem(AUTH_KEY, JSON.stringify(s));
  window.dispatchEvent(new Event(AUTH_EVENT));
}

export function clearSession(): void {
  localStorage.removeItem(AUTH_KEY);
  window.dispatchEvent(new Event(AUTH_EVENT));
}

// ---------- 登录 / 校验 / 注销 ----------

export async function login(username: string, password: string): Promise<AuthSession> {
  const res = await authRequest<{ token: string; user: AuthUser }>('/api/auth/login', {
    body: { username, password }, timeoutMs: 15000,
  });
  const s: AuthSession = { token: res.token, user: res.user, savedAt: Date.now() };
  saveSession(s);
  return s;
}

/** 校验 token；401/失效返回 null，网络故障抛 AuthApiError(status=0) */
export async function verifySession(token: string): Promise<AuthUser | null> {
  try {
    const res = await authRequest<{ user: AuthUser }>('/api/auth/me', { token });
    return res.user;
  } catch (e) {
    if (e instanceof AuthApiError && e.status === 401) return null;
    throw e;
  }
}

export async function logout(token: string): Promise<void> {
  try { await authRequest('/api/auth/logout', { token, body: {} }); } catch { /* 服务器不可达也照常本地注销 */ }
  clearSession();
}

// ---------- 账号管理（仅 boss）----------

export interface AdminUser {
  id: number;
  username: string;
  role: string;
  displayName: string;
  createdAt?: string;
}

export function adminListUsers(token: string): Promise<{ users: AdminUser[] }> {
  return authRequest('/api/admin/users', { token });
}

export function adminAddUser(
  token: string, username: string, password: string, role: string, displayName: string,
): Promise<{ user: AdminUser }> {
  return authRequest('/api/admin/users', { token, body: { username, password, role, displayName } });
}

export function adminUpdateUser(
  token: string, id: number, patch: { password?: string; role?: string; displayName?: string },
): Promise<{ ok: boolean }> {
  return authRequest('/api/admin/users/update', { token, body: { id, ...patch } });
}

export function adminDeleteUser(token: string, id: number): Promise<{ ok: boolean }> {
  return authRequest('/api/admin/users/delete', { token, body: { id } });
}
